import { Box, Button, Card, Checkbox, Form, FormButtonRow, FormGroup } from '@ksyos/design-system';
import { useId, useState } from 'react';
import type { IPaymentsSearchFilters } from '@frontend/components/interfaces/paymentsSearchFilters';
import type { ValidationErrors } from '@frontend/utils';
import { NoPayoutReason, PayoutType } from '@common/enums';
import { paymentsSearchFilterValidationSchema } from '@common/validation/payments';
import { OrganizationSelection } from '@frontend/components/elements/OrganizationSelection';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForOne } from '@frontend/utils';
import { handleValidatePeriod } from '@frontend/utils/validateDatePeriod';
import { EnumSelect } from '../../../../fields/EnumSelect';
import { ExportStatusFilters } from '../../financing/financingSearchFilters/ExportStatusFilters';
import { PeriodFilter } from '../../financing/financingSearchFilters/PeriodFilter';
import { PaymentsGroupFilter } from './PaymentsGroupFilter';
import { PaymentsPerformerPractitionerFilter } from './PaymentsPerformerPractitionerFilter';
import { PaymentsSearchForFilter } from './PaymentsSearchForFilter';
import { PaymentsServiceFilter } from './PaymentsServiceFilter';

interface IPaymentsSearchFiltersProps {
    isSearchButtonDisabled: boolean;
    onSearch: () => Promise<void>;
    onSetPaymentsSearchFilters: (filters: IPaymentsSearchFilters) => void;
    paymentsSearchFilters: IPaymentsSearchFilters;
}

export function PaymentsSearchFilters(props: IPaymentsSearchFiltersProps) {
    const language = getSelectedLanguage();

    const { onSearch, onSetPaymentsSearchFilters, paymentsSearchFilters } = props;

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const payoutTypeInputId = useId();
    const noPayoutReasonInputId = useId();
    const performerOrganizationInputId = useId();

    return (
        <Card>
            <Card.Body>
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        await onSearch();
                    }}
                    role="search"
                    submitOnEnter={true}
                >
                    <Box maxWidth={'900px'}>
                        <FormGroup label={language.common.processesBetween}>
                            <PeriodFilter
                                endDate={paymentsSearchFilters.endDate}
                                errors={errors}
                                onChangeDate={(name, value) => handleSetFilter({ [name]: value })}
                                onValidate={(name, value) =>
                                    handleValidatePeriod({
                                        datePeriod: {
                                            endDate: name === 'endDate' ? value : paymentsSearchFilters.endDate,
                                            startDate: name === 'startDate' ? value : paymentsSearchFilters.startDate,
                                        },
                                        errors,
                                        schema: paymentsSearchFilterValidationSchema,
                                        setErrors,
                                    })
                                }
                                startDate={paymentsSearchFilters.startDate}
                            />
                        </FormGroup>
                        <PaymentsSearchForFilter
                            errors={errors}
                            onChangeSearchField={(value) => handleSetFilter({ searchField: value })}
                            onChangeSearchText={(value) => handleSetFilter({ searchText: value })}
                            onValidateOne={(value) => handleValidateOne('searchText', value)}
                            searchField={paymentsSearchFilters.searchField}
                            searchText={paymentsSearchFilters.searchText}
                        />

                        <PaymentsGroupFilter
                            groupId={paymentsSearchFilters.groupId}
                            onChangeGroup={(value) => handleSetFilter({ groupId: value })}
                        />
                        <FormGroup
                            htmlFor={performerOrganizationInputId}
                            label={language.organization.organization}
                        >
                            <OrganizationSelection
                                id={performerOrganizationInputId}
                                onChange={async (value) =>
                                    handleSetFilter({
                                        performerOrganizationId: value || undefined,
                                    })
                                }
                                value={paymentsSearchFilters.performerOrganizationId}
                            />
                        </FormGroup>
                        <PaymentsPerformerPractitionerFilter
                            onChangePerformerPractitioner={(value) =>
                                handleSetFilter({ performerPractitionerId: value })
                            }
                            performerPractitionerId={paymentsSearchFilters.performerPractitionerId}
                        />
                        <PaymentsServiceFilter
                            onChangeService={(episodeCategoryId, serviceId) =>
                                handleSetFilter({ episodeCategoryId, serviceId })
                            }
                            serviceOrEpisodeCategoryId={
                                paymentsSearchFilters.serviceId || paymentsSearchFilters.episodeCategoryId
                            }
                        />
                        <FormGroup
                            htmlFor={payoutTypeInputId}
                            label="Activiteit"
                        >
                            <EnumSelect
                                enumObj={PayoutType}
                                id={payoutTypeInputId}
                                isClearable={true}
                                labels={language.payoutType}
                                onChange={(option) =>
                                    handleSetFilter({ activityType: option?.value as PayoutType | undefined })
                                }
                                value={paymentsSearchFilters.activityType}
                            />
                        </FormGroup>
                        <FormGroup
                            htmlFor={noPayoutReasonInputId}
                            label={language.finance.reasonNoPayment}
                        >
                            <EnumSelect
                                enumObj={NoPayoutReason}
                                id={noPayoutReasonInputId}
                                isClearable={true}
                                labels={language.noPayoutReasonEnum}
                                onChange={(option) =>
                                    handleSetFilter({ noPayoutReason: option?.value as NoPayoutReason | undefined })
                                }
                                value={paymentsSearchFilters.noPayoutReason}
                            />
                        </FormGroup>
                        <ExportStatusFilters
                            exportStatus={paymentsSearchFilters.exportStatus}
                            onChangeExportStatus={(value) =>
                                handleSetFilter({
                                    exportStatus: value,
                                })
                            }
                        />
                        <FormGroup>
                            <Checkbox
                                checked={Boolean(paymentsSearchFilters.payoutsOnly)}
                                label={language.common.payoutsOnly}
                                onChange={(event) => {
                                    handleSetFilter({
                                        payoutsOnly: event.target.checked,
                                    });
                                }}
                            />
                        </FormGroup>
                        <FormButtonRow justify="left">
                            <Button
                                isDisabled={props.isSearchButtonDisabled}
                                type="submit"
                            >
                                {language.common.searchRefresh}
                            </Button>
                        </FormButtonRow>
                    </Box>
                </Form>
            </Card.Body>
        </Card>
    );

    function handleSetFilter(filter: Partial<IPaymentsSearchFilters>) {
        onSetPaymentsSearchFilters({ ...paymentsSearchFilters, ...filter });
    }

    function handleValidateOne(
        name: keyof IPaymentsSearchFilters,
        value: IPaymentsSearchFilters[keyof IPaymentsSearchFilters]
    ) {
        const newErrors = updateValidationErrorsForOne(
            paymentsSearchFilterValidationSchema,
            { ...paymentsSearchFilters, [name]: value },
            errors,
            name
        );
        setErrors(newErrors);
    }
}
